import { tx } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { api, formatError } from "@/lib/api";
import { localizeAsset } from "@/lib/assets";
import { AppShell, Loader } from "@/components/AppShell";
import { toast } from "sonner";
import { Search, Heart, Trash2, Download, X, Star, Image as ImageIcon, Printer, FileText } from "lucide-react";

export default function Diario() {
  const [entries, setEntries] = useState(null);
  const [q, setQ] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [opts, setOpts] = useState({ scope: "all", date_from: "", date_to: "", include_images: true, include_full: true, print_optimized: false });
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);
  const { t, i18n } = useTranslation();

  const load = async () => {
    try {
      const { data } = await api.get("/entries", { params: { q, favorite: favOnly } });
      setEntries(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, favOnly]);

  const openEntry = (e) => { setSelected(e); setNote(e.note || ""); setTranslated(null); };

  const translateEntry = async () => {
    const lang = (i18n.language || "it").slice(0, 2);
    if (lang === "it") { toast(t("diary.alreadyIt", tx("Il testo è già in italiano"))); return; }
    setTranslating(true);
    try {
      const { data } = await api.post("/translate", { texts: [selected.user_response], target: lang });
      setTranslated(data.translations?.[0] || "");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setTranslating(false); }
  };

  const toggleFav = async (e) => {
    try {
      const { data } = await api.patch(`/entries/${e.id}`, { favorite: !e.favorite });
      setEntries((prev) => prev.map((x) => (x.id === e.id ? data : x)));
      if (selected?.id === e.id) setSelected(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const saveNote = async () => {
    try {
      const { data } = await api.patch(`/entries/${selected.id}`, { note });
      setEntries((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      setSelected(data);
      toast.success(tx("Nota salvata"));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const remove = async (e) => {
    if (!window.confirm(tx("Eliminare questa pagina del diario?"))) return;
    try {
      await api.delete(`/entries/${e.id}`);
      setEntries((prev) => prev.filter((x) => x.id !== e.id));
      setSelected(null);
      toast.success(tx("Pagina eliminata"));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.post("/entries/export/pdf", opts, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : "Ascolta-Anima_Diario.pdf";
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setShowExport(false);
      toast.success(tx("Diario scaricato in PDF"));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Esportazione non riuscita");
    } finally { setExporting(false); }
  };

  if (!entries) return <Loader />;

  return (
    <AppShell>
      <div className="px-7 pt-12">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-4xl text-anima-ink">{tx("Il tuo diario")}</h1>
          <button data-testid="export-pdf-btn" onClick={() => setShowExport(true)} className="p-2.5 rounded-full bg-white/60 border border-anima-ink/10 text-anima-ink">
            <Download size={18} strokeWidth={1.7} />
          </button>
        </div>

        <div className="flex items-center gap-2 rounded-full bg-white/60 border border-anima-ink/10 px-4 py-2.5 mb-3">
          <Search size={17} className="text-anima-muted" />
          <input
            data-testid="diario-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={tx("Cerca per parola o tema...")}
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
        <button
          data-testid="diario-fav-filter" onClick={() => setFavOnly((v) => !v)}
          className={`mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors duration-300 ${favOnly ? "bg-anima-pink border-anima-pink text-anima-ink" : "bg-white/50 border-anima-ink/10 text-anima-muted"}`}
        >
          <Star size={14} />{tx("Preferiti")}</button>

        {entries.length === 0 ? (
          <div className="text-center py-20 text-anima-muted">
            <p className="font-serif text-2xl mb-2">{tx("Ancora nessuna pagina")}</p>
            <p className="text-sm">{tx("Le tue riflessioni appariranno qui, giorno dopo giorno.")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {entries.map((e, i) => (
              <motion.button
                key={e.id} data-testid={`diario-entry-${e.id}`}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05, duration: 0.5 }}
                onClick={() => openEntry(e)}
                className="w-full text-left rounded-3xl bg-white/55 border border-anima-ink/5 p-5 flex gap-4"
              >
                {(e.ai_response?.symbol_image || e.symbol_image) ? (
                  <img src={localizeAsset(e.ai_response?.symbol_image || e.symbol_image)} alt="" className="w-14 h-14 rounded-2xl object-cover shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-2xl bg-anima-pink/60 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-anima-muted">{e.date}</p>
                    {e.favorite && <Heart size={14} className="text-anima-pink fill-anima-pink" />}
                  </div>
                  {e.source === "symbol" && e.symbol_name && (
                    <p className="text-[11px] uppercase tracking-widest text-anima-golddeep">{tx("Simbolo ·")} {e.symbol_name}</p>
                  )}
                  <p className="font-serif text-lg text-anima-ink leading-tight truncate">{e.question_text}</p>
                  <p className="text-sm text-anima-muted truncate">{e.user_response}</p>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-center bg-anima-ink/30 backdrop-blur-sm" onClick={() => setSelected(null)}>
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            onClick={(ev) => ev.stopPropagation()}
            className="w-full max-w-md bg-anima-ivory rounded-t-3xl mt-16 overflow-y-auto no-scrollbar"
            data-testid="diario-detail"
          >
            <div className="sticky top-0 bg-anima-ivory/90 backdrop-blur px-6 py-4 flex items-center justify-between border-b border-anima-ink/5">
              <span className="text-sm text-anima-muted">{selected.date}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => toggleFav(selected)} data-testid="detail-fav-btn" className="p-2">
                  <Heart size={19} className={selected.favorite ? "text-anima-pink fill-anima-pink" : "text-anima-muted"} />
                </button>
                <button onClick={() => remove(selected)} data-testid="detail-delete-btn" className="p-2 text-anima-muted"><Trash2 size={18} /></button>
                <button onClick={() => setSelected(null)} className="p-2 text-anima-muted"><X size={20} /></button>
              </div>
            </div>
            <div className="px-6 py-5 space-y-5 pb-10">
              <p className="font-serif text-2xl text-anima-ink leading-tight">{selected.question_text}</p>
              {selected.source === "symbol" && selected.symbol_name && (
                <div className="flex items-center gap-3 rounded-2xl bg-anima-sage/15 border border-anima-ink/5 p-3" data-testid="diario-detail-symbol">
                  {selected.symbol_image && <img src={localizeAsset(selected.symbol_image)} alt="" className="w-12 h-12 rounded-xl object-cover" />}
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-anima-golddeep">{tx("Simbolo")}</p>
                    <p className="font-serif text-lg text-anima-ink leading-none">{selected.symbol_name}</p>
                  </div>
                </div>
              )}
              <p className="font-serif italic text-lg text-anima-ink/90">"{selected.user_response}"</p>
              {selected.user_response && (
                translated !== null ? (
                  <div data-testid="diary-translation" className="rounded-2xl bg-anima-sage/10 border border-anima-ink/5 p-3">
                    <p className="text-[11px] uppercase tracking-widest text-anima-golddeep mb-1">{t("diary.translatedNote", tx("Traduzione automatica — il tuo testo resta invariato"))}</p>
                    <p className="text-anima-ink/90 leading-relaxed">{translated}</p>
                    <button onClick={() => setTranslated(null)} data-testid="diary-show-original" className="text-sm text-anima-sagedeep mt-2">{t("diary.showOriginal", tx("Mostra originale"))}</button>
                  </div>
                ) : (
                  <button onClick={translateEntry} disabled={translating} data-testid="diary-translate-btn" className="text-sm text-anima-sagedeep disabled:opacity-60">
                    {translating ? t("diary.translating", "Traduzione…") : t("diary.translate", tx("Traduci"))}
                  </button>
                )
              )}
              {selected.ai_response && (
                <div className="space-y-4">
                  {[
                    [tx("Cio che emerge"), selected.ai_response.emerges],
                    [tx("Una possibile lettura"), selected.ai_response.reading],
                  ].map(([l, v]) => (
                    <div key={l}>
                      <p className="text-[11px] uppercase tracking-widest text-anima-golddeep mb-1">{l}</p>
                      <p className="text-anima-ink leading-relaxed">{v}</p>
                    </div>
                  ))}
                  {selected.ai_response.symbol_image && (
                    <div className="rounded-3xl overflow-hidden bg-white/50 border border-anima-ink/5">
                      <img src={localizeAsset(selected.ai_response.symbol_image)} alt="" className="w-full h-44 object-cover" />
                      <div className="p-3">
                        <p className="text-[11px] uppercase tracking-widest text-anima-golddeep mb-1">{tx("Il simbolo emerso dalla tua risposta")}</p>
                        <p className="font-serif text-xl text-anima-ink">{selected.ai_response.symbol_name}</p>
                        <p className="text-sm text-anima-muted">{selected.ai_response.symbol_meaning}</p>
                        <p className="text-[12px] text-anima-muted/80 italic mt-1.5">{tx("Una suggestione simbolica da esplorare liberamente, non un'interpretazione assoluta.")}</p>
                        {selected.ai_response.symbol_is_artwork && (
                          <div className="mt-1.5">
                            <p className="text-[11px] text-anima-muted">{selected.ai_response.symbol_credit || "Dettaglio dell'opera Ianas © Anna Checchi"}</p>
                            <Link to="/ianas" className="text-[12px] text-anima-sagedeep underline">{tx("Scopri la storia di Ianas")}</Link>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-anima-golddeep mb-1">{tx("La domanda che apre")}</p>
                    <p className="font-serif text-lg text-anima-ink">{selected.ai_response.deeper_question}</p>
                  </div>
                  <div className="rounded-2xl bg-anima-sage/20 border border-anima-sage/30 p-4">
                    <p className="text-[11px] uppercase tracking-widest text-anima-golddeep mb-1">{tx("Il passo di oggi")}</p>
                    <p className="text-anima-ink">{selected.ai_response.step}</p>
                  </div>
                </div>
              )}
              <div>
                <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-2">{tx("Nota personale")}</p>
                <textarea
                  data-testid="detail-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                  rows={3} placeholder={tx("Aggiungi un pensiero...")}
                  className="w-full rounded-2xl bg-white/60 border border-anima-ink/10 px-4 py-3 outline-none focus:border-anima-sage"
                />
                <button data-testid="detail-note-save" onClick={saveNote} className="mt-2 rounded-full bg-anima-sagedeep text-anima-ivory px-6 py-2.5 text-sm">{tx("Salva nota")}</button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {showExport && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-anima-ink/30 backdrop-blur-sm px-4 py-6" onClick={() => setShowExport(false)}>
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-anima-ivory rounded-3xl p-6 max-h-[85vh] overflow-y-auto no-scrollbar" data-testid="export-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl text-anima-ink">{tx("Scarica il diario")}</h3>
              <button onClick={() => setShowExport(false)} data-testid="export-close"><X size={20} className="text-anima-muted" /></button>
            </div>

            <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-2">{tx("Cosa includere")}</p>
            <div className="space-y-2 mb-5">
              {[
                ["all", tx("Tutto il diario")], ["last7", tx("Ultimi 7 giorni")], ["last30", tx("Ultimi 30 giorni")],
                ["custom", tx("Intervallo di date")], ["favorites", tx("Solo pagine preferite")],
              ].map(([k, l]) => (
                <button key={k} data-testid={`export-scope-${k}`} onClick={() => setOpts({ ...opts, scope: k })}
                  className={`w-full flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors duration-300 ${opts.scope === k ? "bg-anima-sage/25 border-anima-sage" : "bg-white/50 border-anima-ink/10"}`}>
                  <span className={`w-4 h-4 rounded-full border-2 ${opts.scope === k ? "border-anima-sagedeep bg-anima-sagedeep" : "border-anima-ink/25"}`} />
                  <span className="text-sm text-anima-ink">{l}</span>
                </button>
              ))}
            </div>

            {opts.scope === "custom" && (
              <div className="flex gap-3 mb-5">
                <div className="flex-1">
                  <label className="text-xs text-anima-muted ml-1">{tx("Dal")}</label>
                  <input data-testid="export-date-from" type="date" value={opts.date_from} onChange={(e) => setOpts({ ...opts, date_from: e.target.value })}
                    className="w-full mt-1 rounded-xl bg-white/60 border border-anima-ink/10 px-3 py-2.5 outline-none" />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-anima-muted ml-1">Al</label>
                  <input data-testid="export-date-to" type="date" value={opts.date_to} onChange={(e) => setOpts({ ...opts, date_to: e.target.value })}
                    className="w-full mt-1 rounded-xl bg-white/60 border border-anima-ink/10 px-3 py-2.5 outline-none" />
                </div>
              </div>
            )}

            <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-2">{tx("Opzioni")}</p>
            <div className="space-y-2 mb-6">
              {[
                ["include_images", tx("Includi le immagini dei simboli"), ImageIcon],
                ["include_full", tx("Includi risposte e letture complete"), FileText],
                ["print_optimized", tx("Versione ottimizzata per la stampa"), Printer],
              ].map(([k, l, Icon]) => (
                <button key={k} data-testid={`export-opt-${k}`} onClick={() => setOpts({ ...opts, [k]: !opts[k] })}
                  className="w-full flex items-center gap-3 rounded-2xl border border-anima-ink/10 bg-white/50 px-4 py-3">
                  <Icon size={16} className="text-anima-muted" />
                  <span className="flex-1 text-left text-sm text-anima-ink">{l}</span>
                  <span className={`w-10 h-5.5 rounded-full relative transition-colors duration-300 ${opts[k] ? "bg-anima-sagedeep" : "bg-anima-ink/15"}`} style={{ height: "22px", width: "40px" }}>
                    <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white transition-all duration-300 ${opts[k] ? "left-[20px]" : "left-0.5"}`} />
                  </span>
                </button>
              ))}
            </div>

            <button data-testid="export-confirm" onClick={exportPdf} disabled={exporting}
              className="w-full rounded-full bg-anima-sagedeep text-anima-ivory py-4 font-medium disabled:opacity-60 flex items-center justify-center gap-2">
              <Download size={17} /> {exporting ? tx("Preparo il PDF...") : tx("Scarica PDF")}
            </button>
            <p className="text-[11px] text-anima-muted text-center mt-3">{tx("Il documento contiene solo le tue pagine ed e generato su tua richiesta.")}</p>
          </motion.div>
        </div>
      )}
    </AppShell>
  );
}
